/**
 * Lógica de domínio pura do Compasso (sem dependência de storage).
 * Calcula derivados por experiência, tendência honesta com poucos dados, o efeito
 * regulatório (acalmar/ativar) e a fila de rotina musical.
 *
 * Espelha src/domain.ts do módulo alimentar — mesma filosofia: não enganar com
 * ruído, nunca rebaixar uma favorita marcada à mão, honrar a escolha da criança.
 */
import type { Tendencia } from "../types";
import {
  DerivadosExperiencia,
  EfeitoRegulatorio,
  EstadoRegulacao,
  ExperienciaMusical,
  MomentoMusical,
  PassoEngajamento,
  StatusExperiencia,
} from "./types";

const DIA_MS = 24 * 60 * 60 * 1000;

/** Nº mínimo de momentos pra arriscar uma direção/efeito. Abaixo disso: "poucos_dados". */
export const MIN_MOMENTOS_TENDENCIA = 3;

/**
 * Tendência honesta do engajamento. Com poucos pontos, NÃO promete direção.
 * Compara a média dos passos da metade mais recente vs. a mais antiga (limiar ±0,75).
 */
export function calcularTendenciaEngajamento(momentos: MomentoMusical[]): Tendencia {
  const ords = momentos
    .slice()
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
    .map((m) => m.passo_engajamento as number);

  if (ords.length < MIN_MOMENTOS_TENDENCIA) return "poucos_dados";

  const meio = Math.floor(ords.length / 2);
  const antigos = ords.slice(0, meio);
  const recentes = ords.slice(ords.length - meio);
  const media = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
  const delta = media(recentes) - media(antigos);

  if (delta >= 0.75) return "subindo";
  if (delta <= -0.75) return "caindo";
  return "estavel";
}

/** Eixo de ativação como número: baixo (-1) ↔ regulado (0) ↔ cima (+1). */
const AROUSAL: Record<EstadoRegulacao, number> = {
  desregulado_baixo: -1,
  regulado: 0,
  desregulado_cima: 1,
};

/**
 * Efeito regulatório derivado: compara estado-antes vs. estado-depois dos momentos
 * que registraram AMBOS. Predominância (≥60%) define o rótulo; senão, "variável".
 *  - acalma: baixa a ativação (depois < antes no eixo de arousal)
 *  - ativa:  sobe a ativação (depois > antes)
 *  - regula: traz para o centro (|depois| < |antes|), venha de cima ou de baixo
 */
export function calcularEfeitoRegulatorio(momentos: MomentoMusical[]): EfeitoRegulatorio {
  const pares = momentos.filter(
    (m) => m.confirmado && m.estado_antes && m.estado_depois,
  );
  if (pares.length < MIN_MOMENTOS_TENDENCIA) return "poucos_dados";

  let acalma = 0; // baixou a ativação
  let ativa = 0; // subiu a ativação
  let aoCentro = 0; // chegou mais perto do "regulado"
  for (const m of pares) {
    const a = AROUSAL[m.estado_antes!];
    const d = AROUSAL[m.estado_depois!];
    if (d < a) acalma++;
    else if (d > a) ativa++;
    if (Math.abs(d) < Math.abs(a)) aoCentro++;
  }
  const total = pares.length;
  // bidirecional rumo ao centro (acalma E ativa presentes) = regular, não uma direção só
  if (aoCentro / total >= 0.6 && acalma > 0 && ativa > 0) return "regula";
  if (acalma / total >= 0.6) return "acalma";
  if (ativa / total >= 0.6) return "ativa";
  if (aoCentro / total >= 0.6) return "regula";
  return "variavel";
}

/** Deriva tudo que a UI precisa por experiência a partir dos seus momentos. */
export function calcularDerivadosExperiencia(
  experienciaId: string,
  momentos: MomentoMusical[],
  agora: Date = new Date(),
): DerivadosExperiencia {
  const daExp = momentos
    .filter((m) => m.experiencia_id === experienciaId && m.confirmado)
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora));

  const ultimo = daExp[daExp.length - 1] ?? null;
  const passos = daExp.map((m) => m.passo_engajamento as number);

  return {
    experiencia_id: experienciaId,
    n_momentos: daExp.length,
    ultimo_momento: ultimo?.data_hora ?? null,
    passo_atual: (ultimo?.passo_engajamento as PassoEngajamento) ?? null,
    passo_maximo: passos.length ? (Math.max(...passos) as PassoEngajamento) : null,
    tendencia: calcularTendenciaEngajamento(daExp),
    efeito_regulatorio: calcularEfeitoRegulatorio(daExp),
    dias_desde_ultimo: ultimo
      ? Math.floor((agora.getTime() - new Date(ultimo.data_hora).getTime()) / DIA_MS)
      : null,
  };
}

/**
 * Fila de rotina musical (P0.6): experiências `em_exploracao` não usadas há >=
 * cadência. Ordena pelas mais "esquecidas" primeiro.
 */
export function filaRotinaMusical(
  experiencias: ExperienciaMusical[],
  momentos: MomentoMusical[],
  cadenciaDias: number,
  agora: Date = new Date(),
): Array<{ experiencia: ExperienciaMusical; dias_desde_ultimo: number | null }> {
  return experiencias
    .filter((e) => e.status === "em_exploracao" && !e.arquivado)
    .map((e) => ({
      experiencia: e,
      dias_desde_ultimo: calcularDerivadosExperiencia(e.id, momentos, agora)
        .dias_desde_ultimo,
    }))
    .filter((x) => x.dias_desde_ultimo === null || x.dias_desde_ultimo >= cadenciaDias)
    .sort((a, b) => (b.dias_desde_ultimo ?? 1e9) - (a.dias_desde_ultimo ?? 1e9));
}

/**
 * Sugestão de status derivado. É só sugestão — o cuidador decide. Nunca rebaixa
 * uma `favorita` nem mexe numa `sensivel` marcada à mão (segurança sensorial).
 */
export function statusSugeridoExperiencia(
  atual: StatusExperiencia,
  d: DerivadosExperiencia,
): StatusExperiencia {
  if (atual === "favorita") return "favorita";
  if (atual === "sensivel") return "sensivel";
  if (d.n_momentos === 0) return "nao_usada";
  if ((d.passo_maximo ?? 0) >= PassoEngajamento.Revezamento) return "favorita";
  return "em_exploracao";
}

/**
 * Só pode entrar no modo escolha o que dá pra tocar/ouvir agora e não sobrecarrega:
 * status `favorita`/`em_exploracao`, não arquivado (honrar a escolha — P0.3).
 * `sensivel` fica de fora de propósito (respeitar aversões).
 */
export function elegiveisEscolhaMusical(
  experiencias: ExperienciaMusical[],
): ExperienciaMusical[] {
  return experiencias.filter(
    (e) => !e.arquivado && (e.status === "favorita" || e.status === "em_exploracao"),
  );
}

/** Cria um momento a partir de uma escolha da criança: origem fixa + pendente. */
export function momentoDaCrianca(
  base: Omit<MomentoMusical, "origem" | "confirmado">,
): MomentoMusical {
  return { ...base, origem: "crianca", confirmado: false };
}
